import { BookOutlined, UserOutlined } from "@ant-design/icons";
import { Button } from "antd";
import NotMarkedNew from "Assets/Icons/NotMarkedIconNew";
import { UserInfoContext } from "Context/CommonContext";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  past7Days,
  todayDates,
} from "Pages/MyScheduleBeta/utils/unmarkedLectureHelper";
import React, { useContext } from "react";
import styled from "styled-components";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import useResponsive from "Context/useResponsive";
import { inBetweenTime } from "utilFunctions/shared/ScheduleStatus";

export interface UnmarkedLectureCardProps {
  event: any;
}

const CardContainer = styled.div<{ $isMobile?: boolean }>`
  background-color: #fff9e6;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: ${(props) => (props.$isMobile ? "stretch" : "center")};
  flex-direction: ${(props) => (props.$isMobile ? "column" : "row")};
  gap: ${(props) => (props.$isMobile ? "12px" : "16px")};
  width: 100%;
  box-sizing: border-box;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #92400e;
  word-break: break-word;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const Time = styled.div`
  font-size: 12px;
  color: #d97706;
`;

const Actions = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  gap: 12px;
  width: ${(props) => (props.$isMobile ? "100%" : "auto")};

  & > button {
    flex: ${(props) => (props.$isMobile ? "1" : "initial")};
  }
`;

export const ActionButton = styled<any>(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: #f8fafc;
  }
`;

const formatTime = (dateStr: string) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
};

const UnmarkedLectureCard: React.FC<UnmarkedLectureCardProps> = ({ event }) => {
  const navigate = useNavigate();
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const { isMobile } = useResponsive();
  const isOngoing = inBetweenTime(event?.startTime, event?.endTime);

  const dateViewDatas = moment(event?.endTime)
    .utcOffset("+05:30")
    .format()
    .split("T")[0];
  const time = moment(event?.endTime);

  const checkValidation = () => {
    let retVal = false;
    if (userInfo?.fexRole === "Faculty" && dateViewDatas === todayDates) {
      retVal = false;
    } else if (
      userInfo?.fexRole !== "Faculty" &&
      (time.isBetween(past7Days, todayDates) || dateViewDatas === todayDates)
    ) {
      retVal = false;
    } else {
      retVal = true;
    }
    return retVal;
  };

  const handleCardClick = (eventData: any, targetTab = "details") => {
    navigate(
      `/admin/schedule-beta/lecture-details/${eventData.batchTimeTableId}`,
      {
        state: {
          lecture: eventData,
          selectedSchedule: eventData,
          event: eventData,
          initialTab: targetTab,
        },
      }
    );
  };

  const ViewBtnIcon = () => (
    <NotMarkedNew
      style={{
        position: "absolute",
        width: "14px",
        top: "13px",
        left: "21px",
      }}
    />
  );

  const btnStyle: any = {
    position: "relative",
  };

  return (
    <CardContainer $isMobile={isMobile} onClick={() => handleCardClick(event)}>
      <InfoSection>
        <Title>
          {event.eventName} · {event.batchName}
        </Title>
        <Subtitle>
          <span>{event.subject || "Subject"}</span>
          <span>·</span>
          <span>{event.class || event.room || "Room"}</span>
        </Subtitle>
        <Time>
          {formatTime(event.startTime)} - {formatTime(event.endTime)}
        </Time>
      </InfoSection>
      <Actions $isMobile={isMobile}>
        {event.takeAttendance && (
          <ActionButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleCardClick(event, "attendance");
            }}
            style={btnStyle}
          >
            <UserOutlined />
            <ViewBtnIcon />
            {event.attendanceStatus === "Unmarked"
              ? !checkValidation()
                ? "Mark Attendance"
                : "View Attendance"
              : !checkValidation()
              ? "Edit Attendance"
              : "View Attendance"}
          </ActionButton>
        )}
        {event.markLecture && !isOngoing && (
          <ActionButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleCardClick(event, "syllabus");
            }}
            style={btnStyle}
          >
            <BookOutlined />
            <ViewBtnIcon />
            Mark syllabus
          </ActionButton>
        )}
      </Actions>
    </CardContainer>
  );
};

export default UnmarkedLectureCard;
